// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { color, ellipsis, mergeWithCss, truncateOrBreakLineText, xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import Icon from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

export type TitlePosition = "top" | "bottom";
export type ImageSize = "small" | "medium";
const imageWidths = {
    small: 385,
    medium: 306,
    large: 418,
};
const imageSizes: { [key: string]: { imageWidth: string; imageHeight: string } } = {
    small: {
        imageHeight: "120px",
        imageWidth: `${imageWidths["small"]}px`,
    },
    medium: {
        imageHeight: "180px",
        imageWidth: `${imageWidths["medium"]}px`,
    },
    large: {
        imageHeight: "200px",
        imageWidth: `${imageWidths["large"]}px`,
    },
};
interface OwnProps {
    imageSrc: string;
    title: string;
    description: string;
    link?: string;
    viewTitle?: string;
    openInNewTab?: boolean;
    isCondensed?: boolean;
    titlePosition?: TitlePosition;
    titleVariant?: keyof ThemeTypography;
    imageSize?: ImageSize;
    extendedStyles?: SolutionCardStyles;
}

type Props = OwnProps;

export interface SolutionCardStyles {
    container?: InjectableCss;
    link?: LinkPresentationProps;
    condensedLink?: LinkPresentationProps;
    title?: TypographyProps;
    condensedTitle?: TypographyProps;
    description?: TypographyProps;
    imageWrapper?: ClickablePresentationProps;
    image?: LazyImageProps;
}

export const solutionCardStyles: SolutionCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            height: 100%;
            align-items: ${({ isCondensed }: any) => (isCondensed ? "center" : "flex-start")};
        `,
    },
    title: {
        color: "text",
        css: css`
            margin-bottom: 12px;
            ${ellipsis(2)}
        `,
    },
    condensedTitle: {
        color: "text",
        css: css`
            margin-bottom: ${({ isCondensed, titlePosition }: any) =>
                !isCondensed && titlePosition === "bottom" ? "12px" : "0"};
            text-align: center;
            margin-top: auto;
            ${xScreen("lte", "sm")} {
                ${ellipsis(3)}
            }
            ${ellipsis(2)}
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            flex: 1;
            justify-content: center;
            margin-bottom: ${({ isCondensed, titlePosition }: any) =>
                isCondensed || titlePosition === "bottom" ? "20px" : "12px"};
            max-width: ${({ imageWidth }: any) => imageWidth || "100%"};
            max-height: ${({ imageHeight }: any) => imageHeight || "100%"};
            ${xScreen("lte", "md")} {
                max-height: 168px;
            }
             ${xScreen("lte", "sm")} {
                margin-bottom: ${({ isCondensed }: any) => (isCondensed ? "8px" : "22px")};
                ${({ isCondensed }: any) =>
                    isCondensed &&
                    `
                    min-height: 120px;
                    max-height: 120px;
                    `
                };
            }
        `,
    },
    image: {
        css: css`
            max-width: ${({ imageWidth }: any) => imageWidth || "100%"};
            max-height: ${({ imageHeight }: any) => imageHeight || "100%"};
            & > img {
                height: 100%;
                ${xScreen("lte", "sm")} {
                    ${({ isCondensed }: any) =>
                    isCondensed &&
                    `
                    max-width: 100%;
                    max-height: 100%;
                    height:auto;
                    width:auto;
                    `
                    };            
                }
            }
        `,
    },
    link: {
        color: "primary",
        typographyProps: {
            css: css`
                text-align: center;
                letter-spacing: 0;
                display: flex;
                align-items: center;
                & .link-arrow-right {
                    margin-left: 4px;
                    margin-top: 2px;
                    color: ${color("secondary", 0.55)};
                }
            `,
        },
        css: css`
            margin-top: 20px;
            font-size: ${({ isCondensed }: any) => (isCondensed ? 16 : 15)}px;
            font-weight: ${({ isCondensed }: any) => (isCondensed ? 700 : 600)};
            line-height: ${({ isCondensed }: any) => (isCondensed ? 17 : 26)}px;
            &.title-bottom {
                margin-top: 0;
            }
            ${xScreen("lte", "sm")} {
                ${({ isCondensed }: any) =>
                    isCondensed &&
                    `
                font-size: 14px;
                font-weight: 600;
                line-height: 16px;
                `};
            }
            ${ellipsis(2)}
        `,
    },
    description: {
        as: "p",
        size: 15,
        lineHeight: "26px",
        color: "text.main",
        weight: "normal",
        css: css`
            ${ellipsis(3)}
        `,
    },
};

const DEFAULT_TRUNCATE_AFTER = 40;
const DEFAULT_BREAK_AFTER = 40;

const SolutionCard: React.FC<Props> = ({
    link,
    openInNewTab,
    imageSrc,
    title,
    viewTitle,
    description,
    isCondensed,
    titlePosition,
    titleVariant,
    imageSize,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeWithCss(solutionCardStyles, extendedStyles));
    const imageProps = isCondensed
        ? { imageWidth: "158px", imageHeight: "158px" }
        : { ...imageSizes[imageSize || "small"] };
    const titleProps = isCondensed ? styles.condensedTitle : styles.title;
    return (
        <StyledWrapper {...styles.container} {...{ isCondensed }} data-test-selector={`SolutionCard-${title}`}>
            {!isCondensed && titlePosition === "top" && (
                <Typography {...titleProps} variant={titleVariant}>
                    {title}
                </Typography>
            )}
            <Clickable
                href={link}
                target={openInNewTab ? "_blank" : undefined}
                {...styles.imageWrapper}
                {...{ isCondensed, titlePosition, ...imageProps }}
            >
                <LazyImage
                    src={imageSrc}
                    altText={title}
                    {...styles.image}
                    {...{ isCondensed}}
                    {...imageProps}
                    {...{
                        transformations: { width: isCondensed ? 158 : imageWidths[imageSize || "small"] },
                    }}
                />
            </Clickable>
            {(isCondensed || titlePosition === "bottom") &&
                (isCondensed ? (
                    <Link
                        href={link}
                        target={openInNewTab ? "_blank" : undefined}
                        {...styles.link}
                        className="title-bottom"
                        {...{ isCondensed }}
                        color="text.main"
                        typographyProps={{
                            ...mergeWithCss(styles.link?.typographyProps, {
                                variant: isCondensed ? "h6" : "h3",
                                as: "h3",
                            }),
                        }}
                    >
                        {title}
                    </Link>
                ) : (
                    <Typography variant="h3" {...{ isCondensed, titlePosition }} {...styles.title}>
                        {title}
                    </Typography>
                ))}
            {!isCondensed && (
                <>
                    <Typography {...styles.description}>{description}</Typography>
                    {viewTitle && (
                        <Link href={link} target={openInNewTab ? "_blank" : undefined} {...styles.link}>
                            {truncateOrBreakLineText({
                                text: (viewTitle || "").trim(),
                                truncateAfter: DEFAULT_TRUNCATE_AFTER,
                                breakAfter: DEFAULT_BREAK_AFTER,
                            })}
                            <Icon src={ChevronRight} size={18} className="link-arrow-right" />
                        </Link>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

export default SolutionCard;
